// src/views/examples/EditCustomer.jsx
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Form,
  Input,
  Container,
  Row,
  Col,
  ModalFooter,
} from "reactstrap";
import Header from "components/Headers/Header";
import Swal from "sweetalert2";
import axios from "axios";
import { useSessionStore } from "../stores/sessionStore";

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/+$/, "");
const API_CUSTOMERS = `${API_BASE}/customers`;
const API_TEST_CONNECTION = `${API_BASE}/connections/test`;

const EditCustomer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const actor = useSessionStore((s) => s.profile?.username);

  const row = location.state?.row; // ส่งมาจากหน้า Customer table

  const [submitting, setSubmitting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testStatus, setTestStatus] = useState(null); // null | success | fail

  const [form, setForm] = useState({
    customerId: "",
    customerName: "",
    status: "ACTIVE",
    notes: "",

    // connection info
    connectionId: "",
    connectionName: "",
    host: "",
    port: "1433",
    dbName: "",
    connUsername: "",
    connPassword: "",
  });

  // ✅ preload form จาก row
  useEffect(() => {
    if (!row) return;

    setForm((s) => ({
      ...s,
      customerId: row.customerId || "",
      customerName: row.customerName || "",
      status: (row.status || "ACTIVE").toUpperCase(),
      notes: row.notes || row.detail || "",

      connectionId: row.connectionId || "",
      connectionName: row.connectionName || "",
      host: row.host || "",
      port: row.port || "1433",
      dbName: row.dbName || "",

      // ✅ preload username ถ้ามี (password ไม่ preload)
      connUsername: row.connUsername || row.username || "",
      connPassword: "",
    }));
  }, [row]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const handleCancel = () => navigate("/customer");

  // ✅ Test Connection: POST /connections/test (ต้องมี user+pass)
  const handleTestConnection = async () => {
    try {
      if (!API_BASE)
        throw new Error("REACT_APP_API_BASE is not set in the .env file.");
      if (!actor)
        throw new Error("The creator's username (x-actor) was not found.");
      if (testing) return;

      if (!form.host.trim()) throw new Error("Please enter Host");
      if (!String(form.port).trim()) throw new Error("Please enter Port");
      if (!form.dbName.trim()) throw new Error("Please enter Database Name");

      const u = form.connUsername.trim();
      const p = form.connPassword;

      if (!u || !p)
        throw new Error("Please enter Username and Password to test connection");

      setTesting(true);
      setTestStatus(null);

      const payload = {
        host: form.host.trim(),
        port: Number(form.port),
        databaseName: form.dbName.trim(),
        username: u,
        password: p,
        timeouts: {
          connectionTimeoutMs: 5000,
          requestTimeoutMs: 5000,
        },
      };

      const res = await axios.post(API_TEST_CONNECTION, payload, {
        headers: {
          "Content-Type": "application/json",
          "x-actor": actor,
        },
      });

      const ok =
        (res?.data?.statustext || "").toUpperCase() === "SUCCESS" ||
        res?.data?.ok === true;

      if (!ok) throw new Error(res?.data?.message || "Connection failed");

      setTestStatus("success");
      await Swal.fire({
        icon: "success",
        title: "Success",
        text: "Connected successfully",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (err) {
      setTestStatus("fail");
      await Swal.fire({
        icon: "error",
        title: "Connection Failed",
        text: err?.response?.data?.message || err?.message,
      });
    } finally {
      setTesting(false);
    }
  };

  // ✅ Update: PUT /customers (Username/Password ไม่บังคับกรอก)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    try {
      if (!API_BASE)
        throw new Error("REACT_APP_API_BASE is not set in the .env file.");
      if (!actor)
        throw new Error("The creator's username (x-actor) was not found.");

      if (!form.customerName.trim())
        throw new Error("Please enter Customer Name");
      if (!form.notes.trim()) throw new Error("Please enter Detail");
      if (!form.connectionName.trim())
        throw new Error("Please enter Connection Name");
      if (!form.host.trim()) throw new Error("Please enter Host");
      if (!String(form.port).trim()) throw new Error("Please enter Port");
      if (!form.dbName.trim()) throw new Error("Please enter Database Name");

      // ✅ ไม่บังคับ username/password แต่ถ้ากรอก ต้องกรอกให้ครบคู่
      const u = form.connUsername.trim();
      const p = form.connPassword;
      if ((u && !p) || (!u && p)) {
        throw new Error(
          "Please enter both Username and Password (or leave both blank)",
        );
      }

      setSubmitting(true);

      const connection = {
        connectionId: form.connectionId,
        connectionName: form.connectionName.trim(),
        host: form.host.trim(),
        port: Number(form.port),
        databaseName: form.dbName.trim(),
        isActive: true,
        ...(u ? { username: u } : {}),
        ...(p ? { password: p } : {}),
      };

      const payload = {
        customerName: form.customerName.trim(),
        status: (form.status || "ACTIVE").toUpperCase(),
        notes: form.notes.trim(),
        connection,
      };

      await axios.put(API_CUSTOMERS+`/${form.customerId}`, payload, {
        headers: {
          "Content-Type": "application/json",
          "x-actor": actor,
        },
      });

      await Swal.fire({
        icon: "success",
        title: "Success",
        text: "Update customer successful",
        showConfirmButton: false,
        timer: 1200,
      });

      navigate("/customer");
    } catch (err) {
      await Swal.fire({
        icon: "error",
        title: "Error",
        text:
          err?.response?.data?.message ||
          err?.message ||
          "Update customer failed",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!row) {
    return (
      <>
        <Header />
        <Container className="mt--7" fluid>
          <Row>
            <Col xs="12">
              <Card className="bg-secondary shadow">
                <CardBody className="text-center text-muted py-5">
                  ไม่พบข้อมูลลูกค้า (กรุณากลับไปหน้า Customer แล้วกด Edit ใหม่)
                  <div className="mt-3">
                    <Button color="secondary" onClick={handleCancel}>
                      Back
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </>
    );
  }

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row>
          <Col xs="12">
            <Card className="bg-secondary shadow">
              <CardHeader className="bg-white border-0">
                <Row className="align-items-center">
                  <Col xs="8">
                    <h6 className="mb-0">Edit Customer</h6>
                  </Col>
                </Row>
              </CardHeader>

              <CardBody>
                <Form autoComplete="off" onSubmit={handleSubmit}>
                  {/* ✅ anti-autofill trick */}
                  <input style={{ display: "none" }} autoComplete="username" />
                  <input
                    style={{ display: "none" }}
                    type="password"
                    autoComplete="current-password"
                  />

                  <div className="pl-lg-4">
                    <h6 className="heading-small text-muted mb-4">
                      Customer Information
                    </h6>

                    <Row>
                      <Col lg="12">
                        <FormGroup>
                          <label className="form-control-label required">
                            Customer Name
                          </label>
                          <Input
                            className="form-control-alternative"
                            type="text"
                            name="customerName"
                            value={form.customerName}
                            onChange={onChange}
                            required
                            autoComplete="off"
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    {/* ✅ Status Dropdown */}
                    <Row>
                      <Col lg="12">
                        <FormGroup>
                          <label className="form-control-label required">
                            Status
                          </label>
                          <Input
                            className="form-control-alternative"
                            type="select"
                            name="status"
                            value={form.status}
                            onChange={onChange}
                          >
                            <option value="ACTIVE">ACTIVE</option>
                            <option value="INACTIVE">INACTIVE</option>
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="12">
                        <FormGroup>
                          <label className="form-control-label required">
                            Detail
                          </label>
                          <Input
                            className="form-control-alternative"
                            type="textarea"
                            name="notes"
                            value={form.notes}
                            onChange={onChange}
                            rows={4}
                            style={{ resize: "vertical" }}
                            required
                            autoComplete="off"
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <hr className="my-4" />

                    <h6 className="heading-small text-muted mb-4">
                      Database Connection
                    </h6>

                    <Row>
                      <Col lg="4">
                        <FormGroup>
                          <label className="form-control-label required">
                            Connection Name
                          </label>
                          <Input
                            className="form-control-alternative"
                            type="text"
                            name="connectionName"
                            value={form.connectionName}
                            onChange={onChange}
                            required
                            autoComplete="off"
                          />
                        </FormGroup>
                      </Col>

                      <Col lg="4">
                        <FormGroup>
                          <label className="form-control-label required">
                            Host
                          </label>
                          <Input
                            className="form-control-alternative"
                            type="text"
                            name="host"
                            value={form.host}
                            onChange={onChange}
                            required
                            autoComplete="off"
                          />
                        </FormGroup>
                      </Col>

                      <Col lg="4">
                        <FormGroup>
                          <label className="form-control-label required">
                            Port
                          </label>
                          <Input
                            className="form-control-alternative"
                            type="number"
                            name="port"
                            value={form.port}
                            onChange={onChange}
                            required
                            min={1}
                            max={65535}
                            autoComplete="off"
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="4">
                        <FormGroup>
                          <label className="form-control-label required">
                            Database Name
                          </label>
                          <Input
                            className="form-control-alternative"
                            type="text"
                            name="dbName"
                            value={form.dbName}
                            onChange={onChange}
                            required
                            autoComplete="off"
                          />
                        </FormGroup>
                      </Col>

                      <Col lg="4">
                        <FormGroup>
                          <label className="form-control-label">
                            Username <span className="text-muted">(optional)</span>
                          </label>
                          <Input
                            className="form-control-alternative"
                            type="text"
                            name="connUsername"
                            value={form.connUsername}
                            onChange={onChange}
                            autoComplete="off"
                            id="conn-username"
                          />
                        </FormGroup>
                      </Col>

                      <Col lg="4">
                        <FormGroup>
                          <label className="form-control-label">
                            Password <span className="text-muted">(optional)</span>
                          </label>
                          <Input
                            className="form-control-alternative"
                            type="password"
                            name="connPassword"
                            value={form.connPassword}
                            onChange={onChange}
                            autoComplete="new-password"
                            id="conn-password"
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <ModalFooter className="px-0 d-flex justify-content-between align-items-center flex-wrap gap-2">
                      <div className="d-flex align-items-center gap-2">
                        <Button
                          color="info"
                          type="button"
                          onClick={handleTestConnection}
                          disabled={testing}
                        >
                          {testing ? "Testing..." : "Test Connection"}
                        </Button>

                        {testStatus === "success" && (
                          <span style={{ color: "#2dce89", fontSize: 20 }}>
                            ✔️
                          </span>
                        )}
                        {testStatus === "fail" && (
                          <span style={{ color: "#f5365c", fontSize: 20 }}>
                            ❌
                          </span>
                        )}
                      </div>

                      <div>
                        <Button
                          color="secondary"
                          onClick={handleCancel}
                          type="button"
                          className="mr-2"
                          disabled={submitting || testing}
                        >
                          Back
                        </Button>

                        <Button
                          color="warning"
                          disabled={submitting || testing}
                          type="submit"
                        >
                          {submitting ? "Updating..." : "Update"}
                        </Button>
                      </div>
                    </ModalFooter>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default EditCustomer;